/**
 * Utility functions for detecting and managing unit states
 */

/**
 * Determines the current state of a unit based on its properties
 * Returns the most important/visible state for display
 * 
 * @param {Object} unit - The unit object with hp, effects, etc.
 * @returns {string|null} - The state to display ('death', 'poisoned', etc.) or null if no special state
 */
export function getUnitState(unit) {
  if (!unit) return null;

  // Death state takes highest priority
  if (unit.hp <= 0) {
    return 'death';
  }

  // Check for status effects in unit.effects
  if (unit.effects && typeof unit.effects === 'object') {
    // Poisoned state
    if (unit.effects.poisoned || unit.effects.poison) {
      return 'poisoned';
    }

    // Stunned state
    if (unit.effects.stunned || unit.effects.stun) {
      return 'stunned';
    }

    // Shielded state
    if (unit.effects.shielded || unit.effects.shield) {
      return 'shielded';
    }

    // Buffed state (generic positive effects)
    if (unit.effects.buffed || unit.effects.empowered || unit.effects.blessed) {
      return 'buffed';
    }

    // Debuffed state (generic negative effects)
    if (unit.effects.debuffed || unit.effects.weakened || unit.effects.cursed) {
      return 'debuffed';
    }
  }

  // Check for status effects in unit.properties (alternative data structure)
  if (unit.properties && typeof unit.properties === 'object') {
    const props = unit.properties;
    
    if (props.poisoned || props.stunned || props.shielded || props.buffed || props.debuffed) {
      // Return first detected effect
      if (props.poisoned) return 'poisoned';
      if (props.stunned) return 'stunned';
      if (props.shielded) return 'shielded';
      if (props.buffed) return 'buffed';
      if (props.debuffed) return 'debuffed';
    }
  }

  // No special state detected
  return null;
}

/**
 * Checks if a unit is death
 * @param {Object} unit - The unit object
 * @returns {boolean} - True if unit is death
 */
export function isUnitdeath(unit) {
  return unit && unit.hp <= 0;
}

/**
 * Checks if a unit is alive
 * @param {Object} unit - The unit object
 * @returns {boolean} - True if unit is alive
 */
export function isUnitAlive(unit) {
  return unit && unit.hp > 0;
}

/**
 * Gets all active states for a unit (for debugging or detailed display)
 * @param {Object} unit - The unit object
 * @returns {Array<string>} - Array of all active state names
 */
export function getAllUnitStates(unit) {
  if (!unit) return [];

  const states = [];

  if (unit.hp <= 0) states.push('death');

  // Check effects object
  if (unit.effects && typeof unit.effects === 'object') {
    Object.keys(unit.effects).forEach(effect => {
      if (unit.effects[effect] && !states.includes(effect)) {
        states.push(effect);
      }
    });
  }

  // Check properties object
  if (unit.properties && typeof unit.properties === 'object') {
    Object.keys(unit.properties).forEach(prop => {
      if (unit.properties[prop] && !states.includes(prop)) {
        states.push(prop);
      }
    });
  }

  return states;
}

/**
 * Determines if a unit state should be shown on the map (vs only in panels)
 * Some states might be too minor or numerous to show on the tactical map
 * 
 * @param {string} state - The state name
 * @returns {boolean} - True if state should be visible on map
 */
export function shouldShowStateOnMap(state) {
  const mapVisibleStates = [
    'death',      // Always show death
    'stunned',   // Important for tactical planning
    'shielded',  // Important for attack planning
  ];

  return mapVisibleStates.includes(state);
}

/**
 * Determines if a unit state should trigger color conversion (like grayscale for death)
 * @param {string} state - The state name
 * @returns {boolean} - True if state should modify unit appearance
 */
export function shouldApplyStateColorConversion(state) {
  const colorConversionStates = [
    'death',      // Grayscale for death units
    // Add other states that need color changes
  ];

  return colorConversionStates.includes(state);
}